import React, { FC, useCallback, useRef, useState } from 'react'
import { IColumn } from 'typescript/interfaces/tableUi'
import TableComponent, { TableComponentType } from 'UI/Table'
import { useTableComponentStyle } from 'UI/Table/style'
import { IScheduleListItem, StatusSchedule } from 'typescript/interfaces/deals'
import { convertDate, convertEmptyField, convertToPriceFormat } from 'utils/formatters'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { deleteSchedule, getDeal, getSchedulesByDeal, updateSchedulePaymentDay } from 'services/deals'
import { useParams } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import { PAYMENT_DAY_EARLIER_THAN_TODAY, SOMETHING_WENT_WRONG } from 'constants/errors'
import { useSnackbar } from 'notistack'
import { ReactComponent as DeleteCircleIcon } from 'assets/svg/DeleteCircleIcon.svg'
import { ReactComponent as EditIcon } from 'assets/svg/EditIcon.svg'
import { ReactComponent as CancelIcon } from 'assets/svg/CloseIcon.svg'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import ModalComponent from 'UI/Modal'
import SchedulePaymentDayForm from 'components/SchedulePaymentDayForm'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'
import { PaymentFrequencyEnum } from 'typescript/interfaces/paymentFrequency'
import { IPaymentDay } from 'typescript/interfaces/paymentDay'
import { AxiosError } from 'axios'

const TableSchedulesLight = TableComponent as TableComponentType<IScheduleListItem>

const SchedulesLightTable: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const queryCache = useQueryCache()
  const { id } = useParams<{ id: string }>()
  const classes = useTableComponentStyle()
  const additionalClasses = useAdditionalMaterialStyle()
  const { data, isLoading } = useQuery(['schedulesDeal', id], () => getSchedulesByDeal(id), {
    retry: false,
    cacheTime: 0,
    enabled: id,
  })

  const [stateDeleteSchedule, setStateDeleteSchedule] = useState({
    id: 0,
    open: false,
  })

  const [scheduleToChangeDay, setScheduleToChangeDay] = useState<IScheduleListItem | null>(null)

  const refetchSchedules = useCallback(() => {
    queryCache.fetchQuery(['inhouse-deal', id], () => getDeal(id))
    queryCache.fetchQuery(['schedulesDeal', id], () => getSchedulesByDeal(id), {
      retry: false,
      cacheTime: 0,
      enabled: id,
    })
  }, [id])

  const [deleteScheduleMut, { isLoading: isLoadingDeleteSchedule }] = useMutation(deleteSchedule, {
    onSuccess: () => {
      refetchSchedules()
      setStateDeleteSchedule({
        open: false,
        id: 0,
      })
      enqueueSnackbar('Schedule successfully deleted')
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const [updatePaymentDayMut, { isLoading: isLoadingUpdatePaymentDay }] = useMutation(updateSchedulePaymentDay, {
    onSuccess: () => {
      refetchSchedules()
      setScheduleToChangeDay(null)
      enqueueSnackbar('Payment day successfully changed')
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar(
        err?.response?.data?.description === 'PAYMENT_DAY_EARLIER_THAN_TODAY' ? PAYMENT_DAY_EARLIER_THAN_TODAY : SOMETHING_WENT_WRONG
      )
    },
  })

  const handleDeleteSchedule = useCallback((idSchedule: number) => {
    setStateDeleteSchedule({
      id: idSchedule,
      open: true,
    })
  }, [])

  const handleChangePaymentDay = useCallback(
    (paymentDay: IPaymentDay) => {
      if (scheduleToChangeDay) updatePaymentDayMut({ scheduleId: scheduleToChangeDay.id, paymentDay })
    },
    [scheduleToChangeDay, updatePaymentDayMut]
  )

  const columnsRef = useRef<IColumn<IScheduleListItem>[]>([
    {
      label: 'Start Date',
      field: 'startDate',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertDate(rowData.startDate),
    },
    {
      label: 'End Date',
      field: 'endDate',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertDate(rowData.endDate),
    },
    {
      label: 'Next Payment Date',
      field: 'nextPaymentDate',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertDate(rowData.nextPaymentDate),
    },
    {
      label: 'Amount',
      field: 'frequentPayment',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertToPriceFormat(rowData.frequentPayment),
    },
    {
      label: 'Term',
      field: 'term',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertEmptyField(rowData.term),
    },
    {
      label: 'Frequency',
      field: 'paymentFrequency',
      bodyCellClassName: classes.accentCell,
      render: (rowData) =>
        rowData.paymentFrequency === PaymentFrequencyEnum.Weekly && rowData.paymentDay
          ? `${rowData.paymentFrequency} (${rowData.paymentDay})`
          : convertEmptyField(rowData.paymentFrequency),
    },
    {
      label: 'Bank Account',
      field: 'bankAccount',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertEmptyField(rowData.bankAccount),
    },
    {
      label: 'Status',
      field: 'status',
      bodyCellClassName: classes.accentCell,
      render: (rowData) => convertEmptyField(rowData.status),
    },
    {
      label: '',
      field: 'delete',
      render: (rowData) => (
        <Box display="flex">
          {rowData.paymentFrequency === PaymentFrequencyEnum.Weekly && rowData.status === StatusSchedule.active && (
            <Tooltip title="Change payment day">
              <IconButton size="small" onClick={() => setScheduleToChangeDay(rowData)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={() => {
              handleDeleteSchedule(rowData.id)
            }}
          >
            <DeleteCircleIcon />
          </IconButton>
        </Box>
      ),
    },
  ])
  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <TableSchedulesLight columns={columnsRef.current} data={data ? data.data : []} />
      )}
      <ModalComponent open={stateDeleteSchedule.open}>
        <ConfirmationContent
          isLoading={isLoadingDeleteSchedule}
          handleCancel={() =>
            setStateDeleteSchedule({
              id: 0,
              open: false,
            })
          }
          handleConfirm={() => deleteScheduleMut(stateDeleteSchedule.id)}
          text="Are you sure you want to delete all schedules?"
        />
      </ModalComponent>
      <ModalComponent open={Boolean(scheduleToChangeDay)}>
        <Box maxWidth={500}>
          <Box display="flex" justifyContent="flex-end" mt="-1rem" mr="-1rem">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => setScheduleToChangeDay(null)}
              classes={{
                root: additionalClasses.closeTrsModalButton,
              }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
          <Typography variant="h2" color="textSecondary">
            Change Payment Day
          </Typography>
          {scheduleToChangeDay && (
            <Box mt="2rem">
              <SchedulePaymentDayForm
                schedule={scheduleToChangeDay}
                cancel={() => setScheduleToChangeDay(null)}
                submit={handleChangePaymentDay}
                loading={isLoadingUpdatePaymentDay}
              />
            </Box>
          )}
        </Box>
      </ModalComponent>
    </>
  )
}

export default SchedulesLightTable
